import React, { useEffect, useMemo, useState } from 'react';
import GraphPopup from './GraphPopup';
import SalesItemRow from './ItemWiseSalesRow';
import NoDataPlaceholder from './NoDataPlaceholder';
import { StandardSearch } from '../styles/standardStyles';
import { sortBySearchPriority } from '../utils/searchPriority';

/**
 * "View Table" behind the Dashboard's Item-Wise Sales Report slide.
 *
 * WHY THIS EXISTS
 * ---------------
 * The slide has room for one figure per item, and that figure is a percentage. A reader
 * looking at "pink pasta 88.3%" cannot see the ₹1,500 of sales, the ₹58.46 a plate costs
 * to make or the ₹1,324.62 of profit behind it. This opens the full report.
 *
 * WHY IT OWNS ALMOST NO MARKUP
 * ----------------------------
 * The Stock Report already publishes this report — same endpoint, same rows, same title.
 * This screen is that screen, opened from the Dashboard: the same GraphPopup shell, the
 * same StandardSearch, the same sortBySearchPriority ranking and the same SalesItemRow
 * for every item, so an item reads identically wherever the reader opens it — photo,
 * price · plates sold, wastage line, revenue, margin, and the expandable
 * Avg sold − Cost = Profit panel with its ingredient list and "View More" breakdown.
 *
 * The first version of this screen had its own table markup instead. It showed the same
 * numbers, correctly, and still read as a different report — which is exactly what a
 * shared row component exists to prevent. Nothing below decides how a row looks or what
 * it calculates; those decisions live in ItemWiseSalesRow.js.
 */

/** Local YYYY-MM-DD. `toISOString()` is UTC and lands on yesterday west of Greenwich. */
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const ItemWiseSalesTableModal = ({ isOpen, onClose, items, language = 'en' }) => {
    const [modalSearch, setModalSearch] = useState('');

    // The search box belongs to one opening of the report, not to the session.
    useEffect(() => {
        if (!isOpen) {
            setModalSearch('');
        }
    }, [isOpen]);

    // Identical ranking to the Stock Report's modal — the same call on the same rows, so
    // a search for "pizza" surfaces the same items in the same order on both screens.
    const filteredItems = useMemo(() => {
        if (!Array.isArray(items)) return [];
        return sortBySearchPriority(items, modalSearch, (item) => item?.name || '');
    }, [items, modalSearch]);

    // Last day of the period on screen, which the ingredient cost breakdown anchors its
    // Week / Month windows to. Every Dashboard period (Today / Week / Month) ends today.
    const anchorDate = useMemo(() => formatLocalDate(new Date()), []);

    const hasItems = Array.isArray(items) && items.length > 0;

    return (
        <GraphPopup
            isOpen={isOpen}
            onClose={onClose}
            title="Item Wise Sales Report"
            extraClass="item-wise-sales-popup"
        >
            {hasItems ? (
                <div className="item-wise-sales-modal-container">
                    {/* Sticky search — the same block, with the same offsets, as the Stock
                        Report's copy of this modal. */}
                    <div style={{
                        position: 'sticky',
                        top: '-16px',
                        background: '#fff',
                        zIndex: 100,
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        marginLeft: '-20px',
                        marginRight: '-20px',
                        paddingLeft: '20px',
                        paddingRight: '20px',
                        marginTop: '-16px',
                        marginBottom: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}>
                        <StandardSearch
                            value={modalSearch}
                            onChange={setModalSearch}
                            onClear={() => setModalSearch('')}
                            placeholder="Search items"
                            showClearButton={true}
                            style={{ marginBottom: '0' }}
                        />
                    </div>

                    {filteredItems.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '300px',
                            color: '#999',
                            fontSize: '15px',
                            textAlign: 'center',
                            padding: '40px 20px'
                        }}>
                            No items match your search
                        </div>
                    ) : (
                        filteredItems.map((item, index) => (
                            <SalesItemRow
                                key={item.menuItemId ?? item.name ?? index}
                                item={item}
                                isLast={index === filteredItems.length - 1}
                                modalSearch={modalSearch}
                                anchorDate={anchorDate}
                                language={language}
                            />
                        ))
                    )}
                </div>
            ) : (
                <NoDataPlaceholder
                    type="sales"
                    title="No item sales yet"
                    subtitle="A breakdown of sales by item appears here after your first sale."
                    minHeight="320px"
                />
            )}
        </GraphPopup>
    );
};

export default ItemWiseSalesTableModal;
